/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import Table from '../../../../shared/table';

import { RootState } from '../../../../../redux/store';
import { setEmployeeRoundsReportSort } from '../../../../../redux/actions';

import { logRoundProfileFromReports } from '../../../../../utils/analyticsHelpers';
import { date } from '../../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { EmpRoundsReport } from '../../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../../shared/table/Table.styles';

interface EmployeeRoundsReportTableProps {
  loading: boolean;
  roundsReportList: EmpRoundsReport[];
  totalTableCount: number;
}
const EmployeeRoundsReportTable: FC<EmployeeRoundsReportTableProps> = ({
  loading,
  roundsReportList,
  totalTableCount
}) => {
  const dispatch = useDispatch();

  const [sortKey, setSortKey] = useState<string>('roundDate');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [active, setActive] = useState<string>('Round Date');

  const { logTrackingEvent } = useAnalyticsApi();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Department':
        key = 'employeeDept';
        break;
      case 'Employee':
        key = 'employeeName';
        break;
      case 'Status':
        key = 'complianceStatus';
        break;
      case 'Round Type':
        key = 'templateName';
        break;
      case 'Rounded By':
        key = 'roundedBy';
        break;
      case 'Round Date':
        key = 'roundDate';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openEmployee = (round: EmpRoundsReport) => {
    logTrackingEvent(logRoundProfileFromReports('employee'));
    navigate('/employees/profile', {
      state: {
        from: pathname,
        employeeId: round.employeeId,
        mode: undefined
      }
    });
  };

  const isTableEmpty = useMemo(
    () => roundsReportList.length === 0,
    [roundsReportList]
  );

  useEffect(() => {
    return () => {
      dispatch(
        setEmployeeRoundsReportSort({ sortKey: 'roundDate', sortDir: 'desc' })
      );
    };
  }, []);

  useEffect(() => {
    dispatch(setEmployeeRoundsReportSort({ sortKey, sortDir }));
  }, [sortDir, sortKey]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Department"
      id="Employee-Rounds-Report-Table-Department-Column"
      unit="column"
      colWidth={24}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Employee"
      id="Employee-Rounds-Report-Table-Employee-Column"
      unit="column"
      colWidth={18}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      Employee
    </CellContainer>,
    <CellContainer
      key="Status"
      id="Employee-Rounds-Report-Table-Status-Column"
      unit="column"
      colWidth={11}
      direction={!(active === 'Status') ? 'none' : sortDir}
      sort
    >
      Status
    </CellContainer>,
    <CellContainer
      key="Round Type"
      id="Employee-Rounds-Report-Table-Round-Type-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Round Type') ? 'none' : sortDir}
      sort
    >
      Round Type
    </CellContainer>,
    <CellContainer
      key="Rounded By"
      id="Employee-Rounds-Report-Table-RoundedBy-Column"
      unit="column"
      colWidth={17}
      direction={!(active === 'Rounded By') ? 'none' : sortDir}
      sort
    >
      Rounded By
    </CellContainer>,
    <CellContainer
      key="Round Date"
      id="Employee-Rounds-Report-Table-Last-Round-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Round Date') ? 'none' : sortDir}
      sort
    >
      Round Date
    </CellContainer>
  ];

  const rows = roundsReportList?.map((round: EmpRoundsReport, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Employee-Rounds-Report-Row-${i}`}
        id={`Employee-Rounds-Report-Row-${round.roundId}`}
        unit="row"
        size="default"
      >
        <CellContainer unit="column" colWidth={24}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            {round.department}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={18}>
          <StyledLabel
            role="button"
            style={{ whiteSpace: 'normal' }}
            // TODO: Are we gonna link to employee?
            onClick={() => openEmployee(round)}
          >
            <div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  color: '#206DA3',
                  fontWeight: 700
                }}
              >
                {round.employee}
              </div>
            </div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={11}>
          <StyledLabel>{round.status}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel>{round.templateName}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={17}>
          <StyledLabel>
            <div>
              <div>{round.roundedBy}</div>
              {/* TODO: are we including rounders id */}
              {/* <div>{round.createdUserId}</div> */}
            </div>
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>
            {round?.date && round.date.length === 14
              ? date.dayOfYr(round?.date)
              : ''}
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      customClass="employee-rounds-report-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      loading={loading}
      totalResults={totalTableCount}
      type="employeeRoundsReport"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    roundsReportFilter: state.ReportReducer.roundsReportFilter
  };
};

export default connect(mapReduxStateToProps)(EmployeeRoundsReportTable);
